import { getUrlForLookbookProduct, ProductUrlProps } from '@app/logic/products';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Product } from '@typings';
import React from 'react';

interface Context {
  lookbookId: string;
  productId: string | undefined;
  variantId: string | undefined;
  isLoadingMoreProducts: boolean;
  nextProductUrl: string | null;
  previousProductUrl: string | null;
  products: Product.Full[];
  isReadOnly: boolean;
  isLightboxOpen: boolean;
  setIsLightboxOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useLookbookDetailsModalContext = (): Context => {
  const context = React.useContext(LookbookDetailsModalContext);
  if (context === null) {
    throw new Error('LookbookDetailsModalContext can not be used outside the scope of LookbookDetailsModalContextProvider');
  }

  return context;
};

const LookbookDetailsModalContext = React.createContext<Context | null>(null);
interface LookbookDetailsModalContextProviderProps {
  lookbookId: string;
  productId: string | undefined;
  variantId: string | undefined;
  products?: Product.Full[];
  isLoadingMoreProducts?: boolean;
  hasNextPage?: boolean;
  isReadOnly?: boolean;
  fetchNextPage?: VoidFunction;
}

export const LookbookDetailsModalContextProvider = ({
  children,
  fetchNextPage,
  hasNextPage,
  isReadOnly = false,
  isLoadingMoreProducts = false,
  products = [],
  lookbookId,
  productId,
  variantId,
}: React.WithChildren<LookbookDetailsModalContextProviderProps>) => {
  const [isLightboxOpen, setIsLightboxOpen] = React.useState(false);
  const getProductUrl = React.useCallback((urlParams: ProductUrlProps) => getUrlForLookbookProduct(urlParams), []);
  const previousProductUrl = React.useMemo(() => {
    if (!isDefined(productId) || isEmpty(products)) {
      return null;
    }

    const currentIndex = products.findIndex(product => product.product === productId);
    if (currentIndex === 0) {
      return null;
    }

    const [firstProduct] = products;
    const firstProductUrl = isDefined(firstProduct) && currentIndex !== 0 ? getProductUrl({ productId: firstProduct.product }) : null;
    const prevProduct = products[currentIndex - 1];

    return isDefined(prevProduct) ? getProductUrl({ productId: prevProduct.product }) : firstProductUrl;
  }, [products, productId, getProductUrl]);

  const nextProductUrl = React.useMemo(() => {
    if (!isDefined(productId) || isEmpty(products)) {
      return null;
    }

    const currentIndex = products.findIndex(product => product.product === productId);
    if (currentIndex === products.length - 1 && hasNextPage) {
      return null;
    }

    const nextProduct = products[currentIndex + 1];

    return isDefined(nextProduct) ? getProductUrl({ productId: nextProduct.product }) : null;
  }, [products, productId, getProductUrl, hasNextPage]);

  React.useEffect(() => {
    if (!isDefined(productId) || !hasNextPage) {
      return;
    }

    const currentIndex = products.findIndex(product => product.product === productId);
    if (currentIndex === -1) {
      return;
    }

    if (currentIndex > products.length - 2 && isDefined(fetchNextPage)) {
      fetchNextPage();
    }
  }, [fetchNextPage, productId, products, hasNextPage]);

  return (
    <LookbookDetailsModalContext.Provider
      value={{
        isLightboxOpen,
        isLoadingMoreProducts,
        isReadOnly,
        lookbookId,
        nextProductUrl,
        previousProductUrl,
        productId,
        products,
        setIsLightboxOpen,
        variantId,
      }}
    >
      {children}
    </LookbookDetailsModalContext.Provider>
  );
};
